/**
 * ReplyQuote — the WhatsApp-style quote a reactor's bubble shows above its text when it
 * responds to one message (decision M). Reads `metadata.replyTo.sequenceIndex`, resolves
 * it against the loaded thread and shows the target author plus its first line, cut to
 * one line by CSS. Click scrolls to the target bubble. Renders nothing when the target
 * is not in the loaded thread.
 */

import { useTranslation } from 'react-i18next'

import { scrollToChatMessage } from '@/components/chat/RoomTopics/scrollToChatMessage'

import { ChatRoomMessageDto, ChatRoomParticipantDto, RoomMessageReplyTo } from '@digital-twins/shared-types'

export interface ReplyQuoteTarget {
  messageId: string
  /**
   * Null for the user's own message and for the assistant: the component shows the
   * localized "You" or the localized assistant name (`isAssistant`).
   */
  authorName: string | null
  /** True when the quoted message is the Insights assistant's (senderType 'george'). */
  isAssistant: boolean
  firstLine: string
}

const DOCUMENT_END_MARKER = '[/Document]'

/** `metadata.replyTo` when well formed, else null. */
export function readReplyTo(metadata: unknown): RoomMessageReplyTo | null {
  if (!metadata || typeof metadata !== 'object') {
    return null
  }
  const replyTo = (metadata as Record<string, unknown>).replyTo
  if (!replyTo || typeof replyTo !== 'object') {
    return null
  }
  const sequenceIndex = (replyTo as Record<string, unknown>).sequenceIndex
  if (typeof sequenceIndex !== 'number' || !Number.isInteger(sequenceIndex)) {
    return null
  }
  return { sequenceIndex }
}

/** First non-empty line, without the inlined PDF block or markdown markers. */
export function firstLineOf(content: string): string {
  const endIndex = content.lastIndexOf(DOCUMENT_END_MARKER)
  const text = endIndex === -1 ? content : content.slice(endIndex + DOCUMENT_END_MARKER.length)
  const line = text.split('\n').map((part) => part.trim()).find((part) => part.length > 0) ?? ''
  return line
    .replace(/^(#{1,6}\s+|>\s*|[-*+]\s+|\d+[.)]\s+)/, '')
    .replace(/\[([^\]]+)\]\([^)\s]*\)/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*\s](?:[^*]*[^*\s])?)\*/g, '$1')
    .replace(/(^|[^\w])_([^_\s](?:[^_]*[^_\s])?)_(?![\w])/g, '$1$2')
    .trim()
}

/**
 * Null for the user's own message and for the assistant (both are localized by the
 * component), undefined when the author is gone (no quote is shown).
 */
function resolveAuthorName(
  target: ChatRoomMessageDto,
  participants: Array<ChatRoomParticipantDto>,
): string | null | undefined {
  if (target.senderType === 'user') {
    return null
  }
  if (target.senderType === 'george') {
    return null
  }
  const participant = participants.find((p) => p.id === target.senderParticipantId)
  return participant?.displayName ?? undefined
}

/** The quoted message for `metadata`, or null when it has no replyTo or the target is not loaded. */
export function resolveReplyQuoteTarget(
  metadata: unknown,
  messages: Array<ChatRoomMessageDto>,
  participants: Array<ChatRoomParticipantDto>,
): ReplyQuoteTarget | null {
  const replyTo = readReplyTo(metadata)
  if (!replyTo) {
    return null
  }
  const target = messages.find((message) => message.sequenceIndex === replyTo.sequenceIndex)
  if (!target || target.senderType === 'system') {
    return null
  }
  const firstLine = firstLineOf(target.content)
  if (!firstLine) {
    return null
  }
  const authorName = resolveAuthorName(target, participants)
  if (authorName === undefined) {
    return null
  }
  return {
    messageId: target.id,
    authorName,
    isAssistant: target.senderType === 'george',
    firstLine,
  }
}

interface ReplyQuoteProps {
  metadata: unknown
  messages: Array<ChatRoomMessageDto>
  participants: Array<ChatRoomParticipantDto>
  /** Loads more history when the target is not rendered. */
  onBackfill?: () => void | Promise<void>
}

export function ReplyQuote(props: ReplyQuoteProps) {
  const { metadata, messages, participants, onBackfill } = props

  const { t } = useTranslation('app')

  const target = resolveReplyQuoteTarget(metadata, messages, participants)

  if (!target) {
    return null
  }

  const authorName = target.isAssistant
    ? t('roomThread.assistantName')
    : (target.authorName ?? t('chatChrome.replyQuoteYou'))

  const onClick = () => {
    void scrollToChatMessage(target.messageId, { onBackfill })
  }

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="chat-reply-quote"
      className="mb-2 flex w-full min-w-0 flex-col items-start overflow-hidden rounded-md [contain:inline-size] border-l-2 border-twin-chat-accent bg-twin-chat-fg/5 px-2.5 py-1.5 text-left transition-colors hover:bg-twin-chat-fg/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-twin-chat-accent/50"
    >
      <span className="sr-only">{t('chatChrome.replyQuotePrefix')} </span>
      <span
        className="max-w-full truncate text-xs font-medium text-twin-chat-accent"
        data-testid="chat-reply-quote-author"
      >
        {authorName}
      </span>
      <span className="sr-only">, </span>
      <span
        className="block w-full truncate text-sm text-twin-chat-muted"
        data-testid="chat-reply-quote-text"
      >
        {target.firstLine}
      </span>
    </button>
  )
}
